import React, { useState, useEffect, useRef } from "react";

const CARDS = [
  {
    id: 1,
    image: "/images/physical-ai/agv-2ton-case.jpg",
    alt: "2 TON AGV at Mechano Industry site",
    quote: 'XP deployed 2 ton AGVs at our site which made our journey movement effortless."',
    name: "Pradeep",
    company: "Mechano Industry",
    logo: "/images/physical-ai/logo-mechano.png",
    logoAlt: "Mechano Industry Logo",
  },
  {
    id: 2,
    image: "/images/physical-ai/inwards-device-case.jpg",
    alt: "Inwards device scanner at Lux Industries site",
    quote: '"We are very happy with how fast the solution was integrated into our system. Our throughput has improved by 60% after using the inwards device from XP Robotics."',
    name: "Kaushal Pasari",
    company: "Lux Industries",
    logo: "/images/physical-ai/logo-lux.png",
    logoAlt: "Lux Industries Logo",
  },
  {
    id: 3,
    image: "/images/physical-ai/miracle-amr-case.jpg",
    alt: "XP Robotics AMR at Miracle Electronics site",
    quote: '“We are using XP Robotics AMR’s for material movement and our production has become streamlined because of it.”',
    name: "Sidhartha Vadhar",
    company: "Miracle Electronics Pvt. Ltd.",
    logo: "/images/physical-ai/logo-miracle.png",
    logoAlt: "Miracle Electronics Pvt. Ltd. Logo",
  },
  {
    id: 4,
    image: "/images/physical-ai/ace-inspection-case.jpg",
    alt: "Inspection system at Ace Designs site",
    quote: '“After using XP’s inspection system to detect faults in the products, our rejection rate from customer has drastically reduced.”',
    name: "Rohith",
    company: "Production Manager",
    logo: "/images/physical-ai/logo-ace.png",
    logoAlt: "Ace Designs Logo",
  },
];

export default function PhysicalAiTestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeDot, setActiveDot] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isDesktop, setIsDesktop] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  // Track responsive screen size
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Sequence state machine implementation
  // State 0: Cards 1 & 2 (Index 0) -> Hold 3.8s
  // State 1: Slide to Cards 2 & 3 (Index 1) -> Hold 3.8s
  // State 2: Fade out -> Snap to Cards 3 & 4 (Index 2) -> Fade in -> Hold 3.8s
  // State 3: Slide all the way left back to Cards 1 & 2 (Index 0) -> Loop repeats
  useEffect(() => {
    if (isPaused) return;

    let timeoutId;

    if (currentIndex === 0) {
      // Step 0: Display Cards 1 & 2 for 3000ms, then slide to Cards 2 & 3
      timeoutId = setTimeout(() => {
        setIsTransitioning(true);
        setCurrentIndex(1);
        setActiveDot(1);
      }, 3000);
    } else if (currentIndex === 1) {
      // Step 1: Display Cards 2 & 3 for 3000ms, then fade transition to Cards 3 & 4
      timeoutId = setTimeout(() => {
        // Start fade out
        setIsFading(true);

        setTimeout(() => {
          // Snap position while invisible
          setIsTransitioning(false);
          setCurrentIndex(2);
          setActiveDot(2);

          // Fade back in
          setTimeout(() => {
            setIsFading(false);
          }, 50);
        }, 250);
      }, 3000);
    } else if (currentIndex === 2) {
      // Step 2: Display Cards 3 & 4. Highlight dot 3 after 1.5s, then slide back left to Cards 1 & 2
      const dotTimeout = setTimeout(() => {
        setActiveDot(3);
      }, 1500);

      timeoutId = setTimeout(() => {
        // Slide left back to initial cards (Index 0)
        setIsTransitioning(true);
        setCurrentIndex(0);
        setActiveDot(0);
      }, 3200);

      return () => {
        clearTimeout(timeoutId);
        clearTimeout(dotTimeout);
      };
    }

    return () => clearTimeout(timeoutId);
  }, [currentIndex, isPaused]);

  // Compute translateX string
  const getTranslateX = () => {
    if (isDesktop) {
      // On desktop, cards are 50% width minus half gap (32px gap)
      // Index 0: 0
      // Index 1: -(50% + 16px)
      // Index 2: -(100% + 32px)
      if (currentIndex === 0) return "0px";
      if (currentIndex === 1) return "calc(-1 * (50% + 16px))";
      if (currentIndex === 2) return "calc(-1 * (100% + 32px))";
      return "0px";
    } else {
      // Mobile / Tablet: 1 card visible (100% width + 24px gap)
      if (currentIndex === 0) return "0px";
      if (currentIndex === 1) return "calc(-1 * (100% + 24px))";
      if (currentIndex === 2) return "calc(-2 * (100% + 24px))";
      if (currentIndex === 3) return "calc(-3 * (100% + 24px))";
      return "0px";
    }
  };

  const handleDotClick = (dotIndex) => {
    setActiveDot(dotIndex);
    setIsTransitioning(true);
    setIsFading(false);

    if (isDesktop) {
      if (dotIndex === 0) setCurrentIndex(0);
      else if (dotIndex === 1) setCurrentIndex(1);
      else if (dotIndex === 2 || dotIndex === 3) setCurrentIndex(2);
    } else {
      setCurrentIndex(dotIndex);
    }
  };

  return (
    <section
      className="w-full py-16 sm:py-20 lg:py-24 overflow-hidden"
      style={{ backgroundColor: "rgba(244, 251, 255, 1)" }}
    >
      <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
        {/* Header row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 pb-12">
          <div>
            <p className="text-[12px] font-bold tracking-[2.5px] uppercase text-[#94A3B8]">
              REAL-WORLD IMPACT
            </p>
            <h2 className="mt-2 text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight text-[#0B132B]">
              Trusted by industry leaders.
            </h2>
          </div>

          <div className="flex items-center gap-6 lg:max-w-[460px]">
            {/* Vertical divider visible on desktop */}
            <div className="hidden lg:block w-[1px] h-12 bg-[#CBD5E1]/80 shrink-0" />
            <p className="text-[13.5px] sm:text-[14.5px] leading-[22px] text-[#64748B]">
              Deployed across real-world environments, delivering measurable results and meaningful operational impact.
            </p>
          </div>
        </div>

        {/* Carousel viewport */}
        <div
          className="relative w-full overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div
            className={`flex items-stretch gap-6 lg:gap-8 ${
              isTransitioning
                ? currentIndex === 0
                  ? "transition-transform duration-[500ms] ease-out"
                  : "transition-transform duration-[400ms] ease-out"
                : ""
            } ${
              isFading
                ? "opacity-0 transition-opacity duration-[250ms] ease-in-out"
                : "opacity-100 transition-opacity duration-[250ms] ease-in-out"
            }`}
            style={{
              transform: `translateX(${getTranslateX()})`,
            }}
          >
            {CARDS.map((item) => (
              <div
                key={item.id}
                className="w-full lg:w-[calc(50%-16px)] shrink-0 flex flex-col sm:flex-row items-stretch rounded-[22px] p-4 sm:p-5 lg:p-6 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/5 border border-[#E2EDF8]"
                style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
              >
                {/* Left Photo */}
                <div className="w-full sm:w-[220px] md:w-[240px] lg:w-[245px] xl:w-[265px] shrink-0 rounded-[16px] overflow-hidden bg-slate-100 aspect-square">
                  <img
                    src={item.image}
                    alt={item.alt}
                    className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105"
                    loading="lazy"
                  />
                </div>

                {/* Right Content */}
                <div className="flex flex-col justify-between flex-1 mt-5 sm:mt-0 sm:pl-6 pt-1 pb-1">
                  {/* Quote Section */}
                  <div>
                    {/* Quote Icon */}
                    <svg
                      className="w-7 h-7 text-[#007AFF] mb-3"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>

                    <p className="text-[13.5px] sm:text-[14px] leading-[22px] sm:leading-[23px] text-[#334155] font-normal">
                      {item.quote}
                    </p>
                  </div>

                  {/* Divider & Author / Logo Section */}
                  <div className="mt-5">
                    <div className="w-full h-[1px] bg-[#EEF2F6] mb-4" />

                    <div className="flex flex-col gap-1">
                      <h4 className="text-[16px] font-bold text-[#0F172A] leading-tight">
                        {item.name}
                      </h4>
                      <p className="text-[13px] text-[#64748B]">
                        {item.company}
                      </p>
                    </div>

                    {/* Company Logo */}
                    <div className="mt-3 flex items-center h-10">
                      <img
                        src={item.logo}
                        alt={item.logoAlt}
                        className="max-h-8 sm:max-h-9 max-w-[150px] object-contain object-left"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex items-center justify-center gap-2.5 mt-10 sm:mt-12">
          {[0, 1, 2, 3].map((dotIndex) => (
            <button
              key={dotIndex}
              onClick={() => handleDotClick(dotIndex)}
              aria-label={`Go to slide ${dotIndex + 1}`}
              className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                activeDot === dotIndex
                  ? "w-2.5 bg-[#007AFF]"
                  : "w-2.5 bg-[#BFDBFE] hover:bg-[#93C5FD]"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
